export enum SubscriptionSource {
  AppStoreConsumer = 'AppStoreConsumer',
  StripeBusiness = 'StripeBusiness', // Seat-based businesses
  StripeConsumer = 'StripeConsumer',
  StripeOrganization = 'StripeOrganization', // Device or license key-based organizations
  CustomOverride = 'CustomOverride',
  CustomTrial = 'CustomTrial',
  GooglePlayStore = 'GooglePlayStore',
  SamsungGalaxyStore = 'SamsungGalaxyStore'
}

// Narrower than SubscriptionSource so only Android-store sources can back an android_subscriptions row.
export type AndroidSubscriptionSource = SubscriptionSource.GooglePlayStore | SubscriptionSource.SamsungGalaxyStore

/**
 * An enum representing notability's subscription tiers.
 * Legacy users are also known as classic users.
 *
 * The 'legacy' subscription tier is deprecated. Clients should rely on the
 * isClassic value returned by the processAppleReceipt resolver to determine
 * the type of a user's subscription.
 */
export enum SubscriptionTier {
  business_pro = 'business_pro',
  business = 'business',
  pro = 'pro',
  premium = 'premium',
  light = 'light',
  vpp = 'vpp', // DEPRECATED
  legacy = 'legacy', // DEPRECATED
  starter = 'starter' // DEPRECATED
}

export enum BasicTier {
  business_basic = 'business_basic', // State of a business existing without an active subscription
  starter = 'starter',
  classic = 'classic', // One-time purchase of Notability prior to the subscription model
  lifetime = 'lifetime' // One-time purchase of Classic after June 2026; behaves identically to `classic` on clients
}

export enum StripeConsumerSubscriptionTier {
  pro = 'pro',
  premium = 'premium',
  light = 'light'
}

export enum StripeBusinessSubscriptionTier {
  business_pro = 'business_pro',
  business = 'business'
}

export const ReferralTier = {
  premium: SubscriptionTier.premium,
  pro: SubscriptionTier.pro,
  light: SubscriptionTier.light
} as const satisfies Pick<typeof SubscriptionTier, 'premium' | 'pro' | 'light'>

export type ReferralTier = (typeof ReferralTier)[keyof typeof ReferralTier]

export const eligibleReferralTiers: ReferralTier[] = Object.values(ReferralTier)

const eligibleReferralTierSet = new Set<string>(eligibleReferralTiers)

export function isEligibleReferralTier(tier: string): tier is ReferralTier {
  return eligibleReferralTierSet.has(tier)
}

export const REFERRAL_MAX_REDEMPTIONS = 5

export type Period = 'yearly' | 'monthly'
type SubType = 'consumer'
type Tier = 'pro' | 'plus' | 'lite'

// NOTE (jk): This is the deterministic format we currently use for our lookup keys in Stripe. Be
// careful about changing this, because doing so may make production prices inaccessible. Lives here
// (rather than in the api service) because both the backend checkout and the frontend renewal-price
// lookup derive from `referralRenewalPeriod` below and must agree on this shape.
export type LookupKey = `${Period}_${SubType}_${Tier}`

/**
 * The billing interval a redeemed referral guest pass renews at — the single source of truth shared
 * by the backend checkout lookup key (`<period>_consumer_<tier>` in api `referral_endpoint.ts`) and
 * the frontend renewal-price lookup (`fetchReferralRenewalPrice` in common-frontend `referral.ts`).
 * Flip this and both the billed price and the displayed renewal disclaimers follow.
 *
 * Precondition: the matching Stripe price + lookup key (`<period>_consumer_<tier>`) must already
 * exist for every referral tier before changing this value.
 */
export const referralRenewalPeriod: Period = 'monthly'

/** Stripe `recurring.interval` corresponding to {@link referralRenewalPeriod}. */
export const referralRenewalInterval: 'month' | 'year' = referralRenewalPeriod === 'monthly' ? 'month' : 'year'

export const stripeConsumerSubscriptionTiers = new Set<string>([
  StripeConsumerSubscriptionTier.premium,
  StripeConsumerSubscriptionTier.pro,
  StripeConsumerSubscriptionTier.light
])

export const stripeBusinessSubscriptionTiers = new Set<string>([
  StripeBusinessSubscriptionTier.business,
  StripeBusinessSubscriptionTier.business_pro
])

export type PricingTierMember =
  | BasicTier.starter
  | StripeConsumerSubscriptionTier.premium
  | StripeConsumerSubscriptionTier.pro
  | StripeConsumerSubscriptionTier.light
  | StripeBusinessSubscriptionTier.business
  | StripeBusinessSubscriptionTier.business_pro

export const subscriptionTierToPricingMember: Partial<Record<SubscriptionTier, PricingTierMember>> = {
  [SubscriptionTier.starter]: BasicTier.starter,
  [SubscriptionTier.premium]: StripeConsumerSubscriptionTier.premium,
  [SubscriptionTier.pro]: StripeConsumerSubscriptionTier.pro,
  [SubscriptionTier.light]: StripeConsumerSubscriptionTier.light,
  [SubscriptionTier.business]: StripeBusinessSubscriptionTier.business,
  [SubscriptionTier.business_pro]: StripeBusinessSubscriptionTier.business_pro
}

const basicTiers = new Set<string>([BasicTier.starter])

export function isPricingTierMember(value: string): value is PricingTierMember {
  return (
    basicTiers.has(value) || stripeConsumerSubscriptionTiers.has(value) || stripeBusinessSubscriptionTiers.has(value)
  )
}

const subscriptionTierValues = new Set<string>(Object.values(SubscriptionTier))

export function isSubscriptionTier(value: string | undefined): value is SubscriptionTier {
  return !!value && subscriptionTierValues.has(value)
}

export const subscriptionTierStripeMetadata = 'subscription-tier' as const
export const basicTierStripeMetadata = 'basic-tier' as const

export type UserTier = SubscriptionTier | BasicTier

const basicTierValues = new Set<string>(Object.values(BasicTier))

export function isUserTier(value: string | undefined | null): value is UserTier {
  return typeof value === 'string' && (isSubscriptionTier(value) || basicTierValues.has(value))
}

// References: https://www.twobird.com/note/1D7EFsKNp7K98ps7uSOe_i
// Enumerates all front and backend features that are not available to free-tier
// The "classic" tier usage is purely controlled via the frontend
// The Gallery can be gated by the MDM feature for VPP but this is not subscription related
export enum SubscriptionFeatures {
  // The "unlimited edits" feature gated on the front-end
  edits = 'edits',
  // iCloud sync and auto-backup
  backup = 'backup',
  // Has handwriting and math-conversion functionality
  handwriting = 'handwriting',
  // Has transcription functionality
  asr = 'asr',
  // Can use the chatbot feature
  chatbot = 'chatbot',
  // Can generate learn quizzes
  learn_questions = 'learn_questions',
  // Can generate learn summaries
  learn_summaries = 'learn_summaries',
  // Can use the live transcription feature with the relevant quota level
  live_transcription = 'live_transcription',
  // Can use the Youtube transcription import feature
  youtube_transcripts = 'youtube_transcripts',
  // Can use the discussions ("Sage") feature.
  discussions = 'discussions',
  // Can upload imported questions
  learn_question_uploads = 'learn_question_uploads',
  // Can connect MCP clients (Claude, ChatGPT) to the library
  mcp_tools = 'mcp_tools',
  // Can generate Learn podcasts
  learn_podcasts = 'learn_podcasts',

  // Rollup of all Learn cost sources into a single LLMCost-backed quota
  unified_quota = 'unified_quota'
}

// A lifetime window still restarts at the subscriber's most recent purchase; see getQuotaWindowStart.
export enum LearnQuotaWindowType {
  monthly = 'monthly',
  lifetime = 'lifetime'
}

// "Business" users are premium users for all intents and purposes, except that only Business Pro chats.
const BUSINESS_FEATURES: readonly SubscriptionFeatures[] = [
  SubscriptionFeatures.edits,
  SubscriptionFeatures.backup,
  SubscriptionFeatures.handwriting,
  SubscriptionFeatures.asr,
  SubscriptionFeatures.learn_questions,
  SubscriptionFeatures.learn_summaries,
  SubscriptionFeatures.youtube_transcripts,
  SubscriptionFeatures.discussions,
  SubscriptionFeatures.learn_question_uploads,
  SubscriptionFeatures.unified_quota,
  SubscriptionFeatures.mcp_tools
]

// Any user with a subscription coming through this function has a subscriberId
export function subscriptionFeaturesForTier(subscriptionTier: SubscriptionTier | undefined): SubscriptionFeatures[] {
  // Anonymous users do not have any active subscription features
  if (subscriptionTier === undefined) {
    return []
  }

  switch (subscriptionTier) {
    case SubscriptionTier.business_pro:
    case SubscriptionTier.pro:
      return [
        SubscriptionFeatures.edits,
        SubscriptionFeatures.backup,
        SubscriptionFeatures.handwriting,
        SubscriptionFeatures.asr,
        SubscriptionFeatures.chatbot,
        SubscriptionFeatures.learn_questions,
        SubscriptionFeatures.learn_summaries,
        SubscriptionFeatures.live_transcription,
        SubscriptionFeatures.youtube_transcripts,
        SubscriptionFeatures.discussions,
        SubscriptionFeatures.learn_question_uploads,
        SubscriptionFeatures.learn_podcasts,
        SubscriptionFeatures.unified_quota,
        SubscriptionFeatures.mcp_tools
      ]
    case SubscriptionTier.business:
      return [...BUSINESS_FEATURES]
    case SubscriptionTier.premium:
      return [...BUSINESS_FEATURES, SubscriptionFeatures.chatbot]
    // "Legacy" users are "light" users
    case SubscriptionTier.legacy:
    case SubscriptionTier.light:
      return [
        SubscriptionFeatures.edits,
        SubscriptionFeatures.backup,
        SubscriptionFeatures.chatbot,
        SubscriptionFeatures.learn_questions,
        SubscriptionFeatures.learn_summaries,
        SubscriptionFeatures.youtube_transcripts,
        SubscriptionFeatures.learn_question_uploads,
        SubscriptionFeatures.unified_quota
      ]
    case SubscriptionTier.vpp:
      return [SubscriptionFeatures.edits, SubscriptionFeatures.backup, SubscriptionFeatures.handwriting]
    // Starter Tier is the same thing as the "free" tier and has no premium features
    case SubscriptionTier.starter:
    default:
      return [
        SubscriptionFeatures.chatbot,
        SubscriptionFeatures.learn_questions,
        SubscriptionFeatures.learn_summaries,
        SubscriptionFeatures.learn_question_uploads,
        SubscriptionFeatures.youtube_transcripts,
        SubscriptionFeatures.unified_quota
      ]
  }
}
